import { format, isToday, isTomorrow, parseISO } from 'date-fns';
import { type Href, router } from 'expo-router';
import React from 'react';
import {
    BodyLargeRegular,
    Button,
    ButtonVariant,
    Sheet,
    YStack,
} from 'smoothie';

import type { GuidanceSession } from '../types/GuidanceSessions';

interface ConfirmCancelProps {
    session: GuidanceSession;
    onYes: () => void;
    onNo: () => void;
}

const ConfirmCancel = ({ session, onYes, onNo }: ConfirmCancelProps) => {
    const sessionTime = parseISO(session.startTime);
    const message = isToday(sessionTime)
        ? "Are you sure you want to cancel today's session?"
        : isTomorrow(sessionTime)
          ? "Are you sure you want to cancel tomorrow's session?"
          : `Are you sure you want to cancel your session on ${format(
                sessionTime,
                'MMM do', // e.g. "Oct 31st"
            )}?`;

    return (
        <YStack
            alignItems="center"
            paddingHorizontal={4}
            width="100%"
            maxWidth={500}
            alignSelf="center"
            gap={24}
        >
            <BodyLargeRegular>{message}</BodyLargeRegular>
            <YStack gap={8} width="100%" $sm={{ marginTop: 16 }}>
                <Button
                    variant={ButtonVariant.PRIMARY}
                    tone="danger"
                    text="Yes, cancel my session"
                    onPress={onYes}
                />
                <Button
                    variant={ButtonVariant.SECONDARY}
                    text="No, don't cancel"
                    onPress={onNo}
                />
            </YStack>
        </YStack>
    );
};

interface CancelSessionSheetProps {
    session: GuidanceSession;
    isOpen: boolean;
    onClose: () => void;
    doneRoute: Href;
}

const CancelSessionSheet = ({
    session,
    isOpen,
    onClose,
    doneRoute,
}: CancelSessionSheetProps) => {
    return (
        <Sheet isOpen={isOpen} onClose={onClose} showCloseButton={false}>
            <ConfirmCancel
                session={session}
                onYes={() => {
                    onClose();
                    router.navigate({
                        pathname: `/guidance-sessions/cancel-session`,
                        params: {
                            cancelUrl: encodeURI(
                                session.calendlyCancelUrl ?? '',
                            ),
                            doneRoute: doneRoute as string,
                        },
                    });
                }}
                onNo={() => {
                    onClose();
                }}
            />
        </Sheet>
    );
};

export default CancelSessionSheet;
